// Minimal browser Electrum client — one WebSocket, one request-response.
// Speaks JSON-RPC 1.0 with line-delimited responses (Electrum protocol v1.4+).
// We use it for a single job in Phase 1: balance + FT-UTXO listing at an
// address. Real production wallets should use the shell's ServerPool with
// quorum + SPV verification; the mockup is a preview, one honest endpoint is
// enough to display real numbers.
//
// Fallback: if the first endpoint fails to connect within a timeout, we try
// the next. The pool of endpoints comes from ./endpoints.ts (chipnet-only in
// Phase 1). No retries — a stale balance in the UI is bounded by the next
// user action.

import { CHIPNET_ENDPOINTS } from './endpoints';

type PendingCall = { resolve: (v: unknown) => void; reject: (e: Error) => void; };

const CONNECT_TIMEOUT_MS = 5_000;
const CALL_TIMEOUT_MS = 8_000;

export class ElectrumClient {
  private ws: WebSocket | null = null;
  private pending = new Map<number, PendingCall>();
  private buf = '';
  private nextId = 1;
  readonly url: string;

  private constructor(url: string, ws: WebSocket) {
    this.url = url;
    this.ws = ws;
    ws.addEventListener('message', (ev) => this.onMessage(String(ev.data)));
    ws.addEventListener('close', () => this.fail('socket closed'));
    ws.addEventListener('error', () => this.fail('socket error'));
  }

  /** Open a client against the first reachable endpoint from `pool`. Sends the
   *  mandatory `server.version` handshake so downstream RPCs actually reply. */
  static async connectAny(pool: readonly string[] = CHIPNET_ENDPOINTS): Promise<ElectrumClient> {
    let lastErr: Error | null = null;
    for (const url of pool) {
      try {
        const c = await this.connect(url);
        await c.request('server.version', ['deviant-potidaea/0.1', '1.4.3']);
        return c;
      } catch (e) { lastErr = e instanceof Error ? e : new Error(String(e)); }
    }
    throw lastErr ?? new Error('no chipnet endpoints reachable');
  }

  private static connect(url: string): Promise<ElectrumClient> {
    return new Promise((resolve, reject) => {
      const ws = new WebSocket(url);
      const timer = setTimeout(() => { try { ws.close(); } catch {} reject(new Error(`connect timeout: ${url}`)); }, CONNECT_TIMEOUT_MS);
      ws.addEventListener('open', () => {
        clearTimeout(timer);
        resolve(new ElectrumClient(url, ws));
      }, { once: true });
      ws.addEventListener('error', () => { clearTimeout(timer); reject(new Error(`connect failed: ${url}`)); }, { once: true });
    });
  }

  close(): void {
    try { this.ws?.close(); } catch { /* noop */ }
    this.fail('closed');
  }

  /** JSON-RPC call to the electrum server. */
  request<T = unknown>(method: string, params: unknown[] = []): Promise<T> {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return Promise.reject(new Error('electrum: not connected'));
    const id = this.nextId++;
    const msg = JSON.stringify({ id, method, params }) + '\n';
    return new Promise<T>((resolve, reject) => {
      const timer = setTimeout(() => {
        this.pending.delete(id);
        reject(new Error(`electrum: ${method} timeout`));
      }, CALL_TIMEOUT_MS);
      this.pending.set(id, {
        resolve: (v) => { clearTimeout(timer); resolve(v as T); },
        reject: (e) => { clearTimeout(timer); reject(e); },
      });
      this.ws!.send(msg);
    });
  }

  private onMessage(text: string): void {
    // WSS electrum: each WebSocket frame carries one complete JSON message.
    // No newline-delimited framing over WSS (contra plain-TCP electrum).
    // We keep the buffer path as a fallback for servers that stream multiple
    // JSONs per frame separated by \n, but the common case is single-JSON.
    this.buf += text;
    const tryOnce = (chunk: string): boolean => {
      const t = chunk.trim(); if (!t) return true;
      try {
        const msg = JSON.parse(t);
        const p = typeof msg.id === 'number' ? this.pending.get(msg.id) : undefined;
        if (p) {
          this.pending.delete(msg.id);
          if (msg.error) p.reject(new Error(msg.error?.message ?? String(msg.error)));
          else p.resolve(msg.result);
        }
        return true;
      } catch { return false; }
    };
    // First try: is the whole current buffer one complete JSON?
    if (tryOnce(this.buf)) { this.buf = ''; return; }
    // Fallback: split on \n (some servers may still frame that way)
    let idx: number;
    while ((idx = this.buf.indexOf('\n')) >= 0) {
      const line = this.buf.slice(0, idx); this.buf = this.buf.slice(idx + 1);
      tryOnce(line);
    }
  }

  private fail(reason: string): void {
    const err = new Error('electrum: ' + reason);
    for (const p of this.pending.values()) p.reject(err);
    this.pending.clear();
    this.ws = null;
  }
}

// ── high-level queries used by the mockup ───────────────────────────────────

export interface AddressUtxo {
  tx_hash: string;
  tx_pos: number;
  height: number;
  value: number;   // satoshis
  token_data?: {
    amount: string;   // FT amount as decimal string
    category: string; // 64-char hex
    nft?: { commitment?: string; capability?: 'none' | 'mutable' | 'minting' };
  };
}

export interface HistoryEntry {
  tx_hash: string;
  height: number;   // 0 for mempool; negative if any input is unconfirmed
  fee?: number;
}

/** Fetch tx history for an address (Fulcrum + CashTokens compat). Returns
 *  chronological entries { tx_hash, height }. Most recent last per electrum
 *  convention; caller sorts if needed. Empty on failure. */
export async function fetchAddressHistory(client: ElectrumClient, cashaddr: string): Promise<HistoryEntry[]> {
  try {
    const raw = await client.request<HistoryEntry[]>('blockchain.address.get_history', [cashaddr]);
    return Array.isArray(raw) ? raw : [];
  } catch (e) {
    console.warn('[electrum] address.get_history failed:', e instanceof Error ? e.message : e);
    return [];
  }
}

/** Fetch a transaction's raw hex by txid. Server-side `verbose` flag is
 *  false so we get the raw hex — parsing lives in the caller (libauth
 *  `decodeTransaction` handles it cleanly and gives us typed outputs). */
export async function fetchTransactionHex(client: ElectrumClient, txid: string): Promise<string | null> {
  try {
    const r = await client.request<string>('blockchain.transaction.get', [txid, false]);
    return typeof r === 'string' ? r : null;
  } catch (e) {
    console.warn('[electrum] transaction.get(' + txid + ') failed:', e instanceof Error ? e.message : e);
    return null;
  }
}

/** Fetch all UTXOs at a cashaddr (or scripthash-derived key). We use the
 *  address-level RPC because it works with CashTokens-aware servers and returns
 *  the token_data slice on each output. */
export async function fetchAddressUtxos(client: ElectrumClient, cashaddr: string): Promise<AddressUtxo[]> {
  // Try address-form (Fulcrum + CashTokens). If the endpoint doesn't accept
  // include_tokens, retry without it (loses the token slice but still returns BCH).
  try {
    const raw = await client.request<AddressUtxo[]>('blockchain.address.listunspent', [cashaddr, 'include_tokens']);
    return raw ?? [];
  } catch (e) {
    console.warn('[electrum] listunspent(include_tokens) failed:', e instanceof Error ? e.message : e);
    try {
      const raw = await client.request<AddressUtxo[]>('blockchain.address.listunspent', [cashaddr]);
      return raw ?? [];
    } catch (e2) {
      console.warn('[electrum] listunspent(no-tokens) also failed:', e2 instanceof Error ? e2.message : e2);
      return [];
    }
  }
}

export interface AddressBalance {
  /** Sats sitting in plain-BCH UTXOs (no token_data). Spendable freely
   *  in any tx that doesn't need to preserve a token. */
  bchSats: bigint;
  /** Sats that ride along with FT UTXOs — technically owned by the same
   *  address but stuck in the token's UTXO until the FT is spent (redeem,
   *  transfer, burn). Kept SEPARATE from bchSats so the wallet chip
   *  doesn't over-report spendable BCH — an MBA claim moves N sats out
   *  of the plain-BCH pile and INTO the covenant's own reservoir; the
   *  user's freshly-minted FT UTXO carries only a small backing dust
   *  (rate × units for Artemision), so summing everything as "BCH" would
   *  make the balance look identical to pre-swap. Matches the split the
   *  chipnet explorer at design/explorer/index.html uses. */
  ftBackingSats: bigint;
  ftByCategory: Map<string, bigint>;   // category-id (hex) → summed FT units
  /** Per-category sats-in-token-UTXOs so callers can present per-MBA
   *  "backing" values from the actual UTXO shape rather than
   *  units × rateSats (which is a covenant assumption, not a fact). */
  ftBackingByCategory: Map<string, bigint>;
}

export function summariseBalance(utxos: AddressUtxo[]): AddressBalance {
  let bch = 0n;
  let ftBacking = 0n;
  const ft = new Map<string, bigint>();
  const ftBackingByCat = new Map<string, bigint>();
  for (const u of utxos) {
    const v = BigInt(u.value);
    const t = u.token_data;
    if (t && t.category) {
      // Token UTXO — its sats are locked with the FT until the FT itself is
      // spent. Track separately from plain BCH.
      ftBacking += v;
      if (t.amount) {
        const cur = ft.get(t.category) ?? 0n;
        ft.set(t.category, cur + BigInt(t.amount));
      }
      const curBack = ftBackingByCat.get(t.category) ?? 0n;
      ftBackingByCat.set(t.category, curBack + v);
    } else {
      // Plain BCH UTXO
      bch += v;
    }
  }
  return { bchSats: bch, ftBackingSats: ftBacking, ftByCategory: ft, ftBackingByCategory: ftBackingByCat };
}
